import React, { useEffect, useCallback, useRef } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";

export interface ColumnDef<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: string | number;
  sortable?: boolean;
}

/**
 * Standard infinite-scrolling table for the app.
 *
 * Behavioural contract – keep this consistent across every consumer so the
 * pattern stays predictable:
 *
 *   - `data`                – the rows already fetched (e.g. flattened pages
 *                             from `useInfiniteQuery`). Always rendered, even
 *                             while a next-page fetch is in flight.
 *   - `hasMoreData`         – `hasNextPage` from `useInfiniteQuery`.
 *   - `fetchMoreData`       – `fetchNextPage` from `useInfiniteQuery`. Called
 *                             when the user scrolls past the threshold AND
 *                             auto-called once if the loaded rows don't fill
 *                             the scroll container yet.
 *   - `isLoading`           – initial load (no data yet). Shows a centred
 *                             spinner in place of the empty state. Should
 *                             NOT include `isFetchingNextPage`.
 *   - `isFetchingNextPage`  – a subsequent page is being fetched. Shows a
 *                             small bottom-row spinner; existing rows stay
 *                             mounted so scroll position is preserved.
 *
 * Sticky header, alternating row shading, optional sorting, keyboard
 * navigation, and a built-in auto-prefetch when the container isn't yet
 * scrollable are provided here once for all consumers.
 */
interface GenericInfiniteTableProps<T> {
  title?: string;
  data: T[];
  columns: ColumnDef<T>[];
  hasMoreData: boolean;
  fetchMoreData: () => void;
  /** Initial load (no rows yet). Excludes next-page fetches. */
  isLoading: boolean;
  /**
   * A subsequent page is being fetched. Existing rows stay mounted while this
   * is true; a small spinner is shown at the bottom by InfiniteScroll's loader.
   *
   * Pass `useInfiniteQuery().isFetchingNextPage` directly.
   */
  isFetchingNextPage?: boolean;
  onRowClick?: (row: T) => void;
  scrollId?: string;
  toolbar?: React.ReactNode;
  sortBy?: Record<string, "ASC" | "DESC">;
  onSortChange?: (columnKey: string, direction?: "ASC" | "DESC") => void;
  selectedIndex: number | null;
  setSelectedIndex: (index: number | ((prevIndex: number) => number)) => void;
  onSearchStringChange?: (newString: string | ((prevString: string) => string)) => void;
  /** Optional label shown when the last page has been loaded. */
  endMessageLabel?: string;
  /** Optional label shown when initial load finished but no rows match. */
  emptyMessage?: string;
  /** Optional label shown beneath the centred spinner during the initial load. */
  loadingMessage?: string;
  /**
   * Override the index used for the alternating row shading. By default rows
   * alternate one by one; supply this when several consecutive rows form a
   * logical group (e.g. one clinic with multiple doctor rows) and the shading
   * should alternate group by group instead.
   */
  getRowShadeIndex?: (row: T, index: number) => number;
  /** Optional extra styles merged into a row (e.g. dim inactive rows). */
  rowSx?: (row: T, index: number) => Record<string, any> | undefined;
}

const NeutralSortIcon = () => (
  <span style={{ display: "flex", flexDirection: "row", lineHeight: 0 }}>
    <ArrowUpward style={{ fontSize: "1rem", margin: -3 }} />
    <ArrowDownward style={{ fontSize: "1rem", margin: -3 }} />
  </span>
);
const GenericInfiniteTable = <T extends { id: string | number }>({
  title,
  data,
  columns,
  hasMoreData,
  fetchMoreData,
  isLoading,
  isFetchingNextPage = false,
  onRowClick,
  scrollId = "scrollableGenericTableDiv",
  toolbar,
  sortBy,
  onSortChange,
  selectedIndex,
  setSelectedIndex,
  onSearchStringChange,
  endMessageLabel = "Alle Daten geladen",
  emptyMessage = "Keine Ergebnisse gefunden",
  loadingMessage,
  getRowShadeIndex,
  rowSx,
}: GenericInfiniteTableProps<T>) => {
  // Tracks the last selectedIndex we actually scrolled into view. We only
  // auto-scroll when the user explicitly changes selection – not when the data
  // array grows because the next page just arrived (which would otherwise
  // re-trigger the effect and snap the viewport around).
  const lastScrolledIndexRef = useRef<number | null>(null);

  // Keyboard navigation

  //   const handleKeyDown = useCallback(
  //   (e: KeyboardEvent) => {
  //     setSelectedIndex((prev) => {
  //       let next = prev ?? 0;
  //       if (data.length && e.key === "ArrowDown")
  //         next = Math.min((prev ?? -1) + 1, data.length - 1);
  //       else if (data.length && e.key === "ArrowUp") next = Math.max((prev ?? 1) - 1, 0);
  //       else if (data.length && e.key === "PageDown")
  //         next = Math.min((prev ?? -1) + 10, data.length - 1);
  //       else if (data.length && e.key === "PageUp") next = Math.max((prev ?? 1) - 10, 0);
  //       return next;
  //     });
  //     if (e.key === "Enter") {
  //       e.preventDefault();
  //       if (selectedIndex != null) onRowClick?.(data[selectedIndex]);
  //     } else if (
  //       onSearchStringChange &&
  //       e.key.length === 1 &&
  //       (/\p{L}/u.test(e.key) || e.key === " ")
  //     ) {
  //       e.preventDefault();
  //       onSearchStringChange((prev: string) => prev + e.key);
  //     } else if (onSearchStringChange && e.key === "Backspace") {
  //       e.preventDefault();
  //       onSearchStringChange((prev: string) => prev.slice(0, -1));
  //     }
  //   },
  //   [data, onRowClick, onSearchStringChange]
  // );
 const handleKeyDown = useCallback(
  (e: KeyboardEvent) => {
    //  SUPPRESS keyboard navigation when a text input is focused
    const active = document.activeElement;
    if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.getAttribute("contenteditable") === "true")) {
      return;
    }
    setSelectedIndex((prev) => {
      let next = prev ?? 0;
      if (data.length && e.key === "ArrowDown")
        next = Math.min(next + 1, data.length - 1);
      else if (data.length && e.key === "ArrowUp")
        next = Math.max(next - 1, 0);
      else if (data.length && e.key === "PageDown")
        next = Math.min(next + 10, data.length - 1);
      else if (data.length && e.key === "PageUp")
        next = Math.max(next - 10, 0);
      return next;
    });

    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex != null) onRowClick?.(data[selectedIndex]);
    }

    // Search typing
    if (
      onSearchStringChange &&
      e.key.length === 1 &&
      (/\p{L}/u.test(e.key) || e.key === " ")
    ) {
      e.preventDefault();
      onSearchStringChange((prev: string) => prev + e.key);
    } else if (onSearchStringChange && e.key === "Backspace") {
      e.preventDefault();
      onSearchStringChange((prev: string) => prev.slice(0, -1));
    }
  },
  [data, onRowClick, onSearchStringChange, selectedIndex]
);



  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Auto-scroll to selected row, but only when the user actually changes the
  // selection (e.g. arrow keys, row click) – not on every data-length change.
  useEffect(() => {
    if (selectedIndex == null || selectedIndex < 0) return;
    if (lastScrolledIndexRef.current === selectedIndex) return;
    const el = document.getElementById(`${scrollId}-row-${selectedIndex}`);
    el?.scrollIntoView({ block: "nearest" });
    lastScrolledIndexRef.current = selectedIndex;
  }, [selectedIndex, scrollId]);

  // Auto-prefetch when the container isn't tall enough to be scrollable yet
  // (e.g. first page fits on screen). Without this, InfiniteScroll never fires
  // `next` because the user can never reach the threshold by scrolling.
  useEffect(() => {
    if (!hasMoreData || isLoading || isFetchingNextPage) return;
    const el = document.getElementById(scrollId);
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1) {
      const id = requestAnimationFrame(() => fetchMoreData());
      return () => cancelAnimationFrame(id);
    }
  }, [data.length, hasMoreData, isLoading, isFetchingNextPage, fetchMoreData, scrollId]);

  const showInitialSpinner = isLoading && data.length === 0;
  const showEmptyState = !isLoading && !isFetchingNextPage && data.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%" }}>
      {title && (
        <Typography variant="h6" sx={{ p: 2 }}>
          {title}
        </Typography>
      )}
      {toolbar && <Box sx={{ mb: 1, flexShrink: 0 }}>{toolbar}</Box>}

      {/* Single table with sticky header inside the scroll container so the
          header column widths follow the body's auto-sized content. */}
      <TableContainer id={scrollId} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <InfiniteScroll
          dataLength={data.length}
          hasMore={hasMoreData}
          next={() => {
            // Guard against react-infinite-scroll-component re-firing while a
            // page is already in flight (it can call `next` multiple times as
            // the user keeps scrolling past the threshold).
            if (isFetchingNextPage || isLoading || !hasMoreData) return;
            fetchMoreData();
          }}
          loader={
            isFetchingNextPage ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : null
          }
          endMessage={
            !hasMoreData && data.length > 0 ? (
              <Box sx={{ textAlign: "center", py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {endMessageLabel}
                </Typography>
              </Box>
            ) : null
          }
          scrollableTarget={scrollId}
          style={{ overflow: "visible" }}
          scrollThreshold={0.85}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={String(col.key)}
                    align={col.align || "left"}
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      cursor: col.sortable ? "pointer" : "default",
                      whiteSpace: "nowrap",
                      py: 0.75,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "white",
                      width: col.width,
                    }}
                    onClick={() => {
                      if (!col.sortable || !onSortChange) return;

                      const currentDirection = sortBy?.[col.key as string];
                      const newDirection =
                        currentDirection === "ASC"
                          ? "DESC"
                          : currentDirection === "DESC"
                          ? undefined
                          : "ASC";

                      onSortChange(col.key as string, newDirection);
                    }}
                  >
                    {col.header}
                    {col.sortable ? (
                      <TableSortLabel
                        active={!!sortBy?.[col.key as string]}
                        direction={
                          sortBy?.[col.key as string] === "DESC"
                            ? "desc"
                            : sortBy?.[col.key as string] === "ASC"
                            ? "asc"
                            : undefined // no direction when unsorted
                        }
                        IconComponent={sortBy?.[col.key as string] ? undefined : NeutralSortIcon}
                        hideSortIcon={false}
                      />
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Existing rows are kept mounted throughout the lifecycle.
                  Initial loading and empty states are dedicated rows so the
                  body never collapses to zero rows while a next page is in
                  flight (which would otherwise reset the scroll position). */}
              {data.map((row, i) => (
                <TableRow
                  id={`${scrollId}-row-${i}`}
                  key={`row-${(row as any).id ?? i}`}
                  onClick={() => {
                    setSelectedIndex(i);
                    onRowClick?.(row);
                  }}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    // Selection uses a soft tint of the secondary (bordo)
                    // palette colour so the brand identity is preserved
                    // without overwhelming the row content. Toned down from
                    // the previous `secondary.light` / `secondary.main`
                    // which made text and link affordances hard to read.
                    backgroundColor:
                      selectedIndex === i
                        ? "rgba(156, 26, 53, 0.08)"
                        : (getRowShadeIndex ? getRowShadeIndex(row, i) : i) % 2 === 0
                        ? "white"
                        : mobiliTheme.palette.grey[100],
                    "&:hover": {
                      backgroundColor:
                        selectedIndex === i
                          ? "rgba(156, 26, 53, 0.16) !important"
                          : "rgba(0, 0, 0, 0.04)",
                    },
                    ...(rowSx?.(row, i) || {}),
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={String(col.key)}
                      align={col.align || "left"}
                      sx={{ fontSize: "0.95rem", width: col.width }}
                    >
                      {col.render ? col.render(row) : String((row as any)[col.key] ?? "-")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {showInitialSpinner && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <CircularProgress size={40} />
                      {loadingMessage && (
                        <Typography variant="body2" color="text.secondary">
                          {loadingMessage}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {showEmptyState && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </TableContainer>
    </Box>
  );
};

export default GenericInfiniteTable;
